import {createAsyncThunk} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';
import {downloadBlob} from '../report/utils';
import {
    TimeTrackingImportPreviewDto,
    TimeTrackingImportRequestDto,
    TimeTrackingImportSummaryDto,
} from './types';

const toFormData = (request: TimeTrackingImportRequestDto): FormData => {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('employeeColumn', request.employeeColumn);
    formData.append('employeeName', request.employeeName ?? '');
    formData.append('dateColumn', request.dateColumn);
    formData.append('hoursColumn', request.hoursColumn);
    formData.append('projectOrAbsenceColumn', request.projectOrAbsenceColumn);
    formData.append('typeColumn', request.typeColumn ?? '');
    formData.append('noteColumn', request.noteColumn ?? '');
    formData.append('tagsColumn', request.tagsColumn ?? '');
    formData.append('entityKindColumn', request.entityKindColumn ?? '');
    formData.append('overtimeColumn', request.overtimeColumn ?? '');
    formData.append('skipDuplicates', String(request.skipDuplicates ?? true));
    formData.append('skipErrors', String(request.skipErrors ?? false));
    formData.append('dataMappings', JSON.stringify(request.dataMappings ?? []));
    return formData;
};

const getFileNameFromResponse = (contentDisposition: unknown, fallback: string): string => {
    if (typeof contentDisposition !== 'string') {
        return fallback;
    }

    const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (encodedMatch?.[1]) {
        return decodeURIComponent(encodedMatch[1].trim());
    }

    const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return plainMatch?.[1]?.trim() || fallback;
};

export const downloadTimeTrackingImportTemplate = createAsyncThunk(
    'time-tracking-import/download-template',
    async (period: {year?: number; month?: number} | undefined): Promise<void> => {
        const now = new Date();
        const year = period?.year ?? now.getFullYear();
        const month = period?.month ?? now.getMonth() + 1;
        const response = await HttpClient.timeTrackingImportApi.downloadTemplate(year, month);
        const fallbackName = `time-tracking-import-template_${year}-${String(month).padStart(2, '0')}.xlsx`;

        downloadBlob(
            response.data,
            getFileNameFromResponse(response.headers?.['content-disposition'], fallbackName)
        );
    }
);

export const previewTimeTrackingImport = createAsyncThunk(
    'time-tracking-import/preview',
    async (request: TimeTrackingImportRequestDto): Promise<TimeTrackingImportPreviewDto> => {
        const response = await HttpClient.timeTrackingImportApi.preview(toFormData(request));
        return response.data;
    }
);

export const importTimeTracking = createAsyncThunk(
    'time-tracking-import/import',
    async (request: TimeTrackingImportRequestDto): Promise<TimeTrackingImportSummaryDto> => {
        const response = await HttpClient.timeTrackingImportApi.runImport(toFormData(request));
        return response.data;
    }
);
